import type { MessageAction, MessageState } from "../../lib/queries/messages";

export const STATE_LABELS: Record<MessageState, string> = {
  pending: "Pending",
  leased: "Sending",
  sent: "Sent",
  failed: "Failed",
  cancelled: "Cancelled",
  suppressed: "Suppressed",
  expired: "Expired",
};

export const STATE_PILL: Record<
  MessageState,
  "info" | "ok" | "err" | "muted" | "warn"
> = {
  pending: "info",
  leased: "info",
  sent: "ok",
  failed: "err",
  cancelled: "muted",
  suppressed: "warn",
  expired: "warn",
};

export const CATEGORY_LABELS: Record<string, string> = {
  transactional: "Transactional",
  marketing: "Marketing",
};

export function labelCategory(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

// Allowed source states per admin action (mirrors
// internal/service/message/actions.go — keep in sync).
export const RETRYABLE_STATES: MessageState[] = ["failed", "cancelled", "expired"];
export const CANCELLABLE_STATES: MessageState[] = ["pending"];
export const REDELIVERABLE_STATES: MessageState[] = [
  "sent",
  "failed",
  "cancelled",
  "suppressed",
];

export interface ActionCopy {
  title: string;
  confirmLabel: string;
  body: (to: string) => string;
  success: string;
  danger?: boolean;
}

/** An action confirmed through the shared dialog; redeliver has its own form. */
export type ConfirmedAction = Exclude<MessageAction, "redeliver">;

/** Copy for the admin action confirm dialogs (retry/cancel/rerender). */
export const ACTION_COPY: Record<ConfirmedAction, ActionCopy> = {
  retry: {
    title: "Retry sending",
    confirmLabel: "Retry",
    body: (to) =>
      `Re-queue the message for ${to}? The attempt counter will be reset and the message will be sent as soon as possible.`,
    success: "The message was re-queued.",
  },
  cancel: {
    title: "Cancel message",
    confirmLabel: "Cancel message",
    body: (to) =>
      `Cancel the pending message for ${to}? The message will not be sent.`,
    success: "The message was cancelled.",
    danger: true,
  },
  rerender: {
    title: "Re-render and deliver",
    confirmLabel: "Re-render",
    body: (to) =>
      `A new message for ${to} will be rendered from the stored data against the current template version. Continue?`,
    success: "The message was re-rendered and queued for delivery.",
  },
};
